
import React, { useState, useEffect, useContext } from 'react';
import { PageContext } from '../components/page';

import PageContentWrapper from '../components/pageContentWrapper';
import {
  getMessageConfigurations,
  patchMessageConfiguration,
} from '../../service/messageConfigurationService';
import type {
  MessageConfigurationResponse,
  PatchMessageConfigurationValuesRequest,
} from '../../types/messageConfiguration';
import MessageConfigurationForm from './MessageConfigurationForm';
import { getRegisteredEmailsAndDomains } from '../../service/emailDomainService';
import responseProcessor from '../components/registeredEmailDomainInputContainer/responseProcessor';
import { getProperty } from '../../fsConfig';

const errors = {
  getMessageConfiguration: {
    backendFailure:
      'Failed to load message configuration. Please contact support or try again later.',
  },
  updateMessageConfiguration: {
    backendFailure:
      'Failed to update message configuration. Please contact support or try again later.',
    nameDuplicateError:
      'Failed to update message configuration. Message configuration with entered name already exists.',
  },
};

const MessageConfigurationContainer = () => {
  const { pushAlert } = useContext(PageContext);
  const user = getProperty('session', {});
  const [loading, setLoading] = useState<boolean>();
  const [hasError, setHasError] = useState(true);
  const [domainList, setDomainList] = useState<string[]>([]);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [messageConfigs, setMessageConfigs] = useState<MessageConfigurationResponse>([]);

  const getConfigs = async () => {
    setLoading(true);
    try {
      const [messageConfigResponse, emailDomainResponse] = await Promise.all([
        getMessageConfigurations(),
        getRegisteredEmailsAndDomains(),
      ]);

      setMessageConfigs(messageConfigResponse);
      const { emailList: responseEmailList, domainList: responseDomainList } = responseProcessor(emailDomainResponse);
      setDomainList(responseDomainList);
      setEmailList(responseEmailList);
      setHasError(false);
    } catch (error) {
      setMessageConfigs([]);
      setHasError(true);
      pushAlert('danger', errors.getMessageConfiguration.backendFailure);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.accountId === '345') {
      pushAlert('conducive');
      setLoading(false);
    } else {
      getConfigs();
    }
  }, []);

  const updateMessageConfiguration = async (
    id: string,
    patchRequest: PatchMessageConfigurationValuesRequest,
  ): Promise<void> => {
    setLoading(true);
    try {
      await patchMessageConfiguration(id, patchRequest);
      setHasError(false);
      await getConfigs();
      pushAlert('success', 'Successfully updated email configuration.');
    } catch (error: any) {
      const message = error.message.endsWith('already exists.')
        ? errors.updateMessageConfiguration.nameDuplicateError
        : errors.updateMessageConfiguration.backendFailure;
      pushAlert('danger', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContentWrapper title="EMAIL CONFIGURATION" loading={loading}>
        {!hasError && (
          <MessageConfigurationForm
            messageConfigs={messageConfigs}
            domainList={domainList}
            emailList={emailList}
            onSave={updateMessageConfiguration}
          />
        )}
      </PageContentWrapper>
  );
};

export default MessageConfigurationContainer;
